// Ubicación en el repo: src/lib/branches.js
// Sucursales — spec-prototipo.md 1.9. CRUD gestionado solo por Admin/
// bootstrap (RLS `branches_write`); el resto de la app solo lee (combos,
// filtros de sucursal activa).
import { supabase } from './supabaseClient';

// Trae las sucursales activas. RLS (`branches_select`) ya filtra
// deleted_at/visibilidad — acá no hace falta repetir esa lógica.
export async function listActiveBranches() {
  const { data, error } = await supabase
    .from('branches')
    .select(
      'id, name, address, phone, logo_path, orl_enabled, oft_enabled, servicios_descripcion, comuna, region, schedule, maps_link_enabled'
    )
    .is('deleted_at', null)
    .order('name', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

// Para la pantalla de administración: Admin también ve las archivadas
// (RLS `branches_select` ya se las deja pasar con `is_admin()`), acá se
// separan en dos grupos para no mezclar en el listado.
export async function listAllBranchesForAdmin() {
  const { data, error } = await supabase
    .from('branches')
    .select(
      'id, name, address, phone, logo_path, orl_enabled, oft_enabled, servicios_descripcion, comuna, region, schedule, deleted_at, maps_link_enabled'
    )
    .order('name', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

// El prototipo fuerza `name` a MAYÚSCULAS al guardar (ver spec-prototipo.md
// 1.9) — se replica acá, no solo confiar en que el usuario ya la escriba así.
// Devuelve el id de la fila creada — lo necesita BranchFormModal para subir
// el logo recién elegido, que solo puede ir a Storage una vez que la
// sucursal existe (la ruta del archivo usa {branch_id}/...).
export async function createBranch({
  name,
  address,
  phone,
  orlEnabled,
  oftEnabled,
  serviciosDescripcion,
  comuna,
  region,
  schedule,
  mapsLinkEnabled,
}) {
  const { data, error } = await supabase
    .from('branches')
    .insert({
      name: name.trim().toUpperCase(),
      address: address?.trim().toUpperCase() || null,
      phone: phone?.trim() || null,
      orl_enabled: orlEnabled,
      oft_enabled: oftEnabled,
      // A diferencia de name/address, NO se fuerza a mayúsculas — es texto
      // libre pensado para mostrarse tal cual en la página pública nueva
      // (DECISIONS.md, 23-ago-2026), no un identificador corto.
      servicios_descripcion: serviciosDescripcion?.trim() || null,
      // comuna/región (migración 0042): solo para la página pública, NO se
      // usan en los documentos imprimibles — mismo motivo, no se fuerzan a
      // mayúsculas.
      comuna: comuna?.trim() || null,
      region: region?.trim() || null,
      // horario semanal + almuerzo (migración 0043) — `schedule` ya viene
      // armado en la forma jsonb de la BD (formToSchedule(), en
      // src/lib/branchSchedule.js), acá no se transforma nada más.
      schedule: schedule ?? null,
      // Link automático de Google Maps sobre la dirección (migración 0045,
      // 24-ago-2026) — desactivar solo en sucursales itinerantes, cuya
      // ubicación cambia y no debe comunicarse sola. `?? true` porque el
      // formulario nuevo trae el checkbox marcado por defecto.
      maps_link_enabled: mapsLinkEnabled ?? true,
    })
    .select('id')
    .single();
  if (error) throw error;
  return data.id;
}

export async function updateBranch(
  id,
  { name, address, phone, orlEnabled, oftEnabled, serviciosDescripcion, comuna, region, schedule, mapsLinkEnabled }
) {
  const { error } = await supabase
    .from('branches')
    .update({
      name: name.trim().toUpperCase(),
      address: address?.trim().toUpperCase() || null,
      phone: phone?.trim() || null,
      orl_enabled: orlEnabled,
      oft_enabled: oftEnabled,
      servicios_descripcion: serviciosDescripcion?.trim() || null,
      comuna: comuna?.trim() || null,
      region: region?.trim() || null,
      schedule: schedule ?? null,
      maps_link_enabled: mapsLinkEnabled ?? true,
    })
    .eq('id', id);
  if (error) throw error;
}

// Habilitado por defecto si el campo todavía no llegó del servidor (ej.
// un `branch` armado a mano en algún punto viejo del código que no pidió
// estas columnas) — mejor no bloquear de más por un default ausente.
// DECISIONS.md #42.
export function isModuleEnabledForBranch(branch, module) {
  if (!branch) return true;
  return module === 'ORL' ? branch.orl_enabled !== false : branch.oft_enabled !== false;
}

// Igual patrón que pacientes/citas: nunca DELETE real, solo deleted_at
// (DECISIONS.md #2) — RLS de `branches` ya bloquea el DELETE real de todas
// formas (no hay policy `for delete`).
export async function softDeleteBranch(id, actorId) {
  const { error } = await supabase
    .from('branches')
    .update({ deleted_at: new Date().toISOString(), deleted_by: actorId })
    .eq('id', id);
  if (error) throw error;
}

export async function restoreBranch(id) {
  const { error } = await supabase
    .from('branches')
    .update({ deleted_at: null, deleted_by: null })
    .eq('id', id);
  if (error) throw error;
}
